<template>
  <main>
    <Trail
      :list="[
        {
          path: '/proyectos/investigacion',
          name: 'Projects',
          tag: 'Proyectos de Investigación'
        },
        {
          name:
            'Impacto de la variabilidad y cambio climático en el ecosistema de manglares de Tumbes',
          tag:
            'Impacto de la variabilidad y cambio climático en el ecosistema de manglares de Tumbes'
        }
      ]"
    />
    <Container class="container--main">
      <Title tag="h1" align="center">
        Proyecto "Impacto de la Variabilidad y Cambio Climático en el Ecosistema
        de Manglares de Tumbes"
      </Title>
      <section>
        <Figure>
          <Image
            fullsize
            :srcsets="['/images/proyectos/manglares/cogollo_camarones2.jpg']"
            title="Cogollo Camarones en la ZA del Santuario Nacional Los Manglares de Tumbes"
          />
          <template #figcaption>
            Cogollo Camarones en la ZA del Santuario Nacional Los Manglares de
            Tumbes
          </template>
        </Figure>
      </section>
      <Row>
        <ColumnUi md="9">
          <Navtab id="resumen">
            <Title tag="h2">Resumen</Title>
            <p>
              El ecosistema de manglares de Tumbes, en la costa norte del Perú,
              está ubicado en la frontera entre las cálidas aguas del Pacífico
              ecuatorial y las aguas frías de la Corriente de Humboldt. Debido a
              esto, es fuertemente afectado por la variabilidad climática
              asociada al Fenómeno El Niño. Este ecosistema es muy importante
              para la región de Tumbes, debido a que es fuente de alimentos y
              trabajo y a los servicios ambientales que provee. Además de los
              fenómenos naturales, también existen presiones por las actividades
              humanas, como la contaminación y deforestación por actividades
              acuícolas. Más alarmantemente, la sobre-explotación de uno de sus
              recursos más valiosos, la concha negra (Anadara tuberculosa), ha
              producido una
              <a
                href="http://www.imarpe.gob.pe/tumbes/especies_comerciales/invertebrados/concha_negra.pdf"
                target="_blank"
                rel="nofollow"
                >reducción en las capturas de 80% sobre los últimos 14 años</a
              >. La gestión sostenible del ecosistema requiere información
              científica básica con la que actualmente no se cuenta, mientras
              que lidiar con los futuros efectos de cambio climático requerirá
              una aún mayor profundidad en el conocimiento de este ecosistema y
              sus controles ambientales.
            </p>
            <p>
              El presente proyecto de investigación intenta llenar este vacío.
              Como es requerido por la naturaleza del proyecto, será llevado a
              cabo por un equipo multi-disciplinario e inter-institucional de
              investigadores en las ciencias físicas, biológicas y sociales, en
              coordinación con los actores y autoridades. Varios estudiantes
              contribuirán en la investigación a través del desarrollo de sus
              tesis universitarias. Un rango de escenarios de la variabilidad y
              cambio climático futuro, así como tendencias socio-económicos,
              serán desarrollados y utilizados en la elaboración de un plan
              integrado de adaptación para el ecosistea de manglares y la
              población asociado.
            </p>
          </Navtab>
          <Navtab id="abstract">
            <Title tag="h2">
              Abstract
            </Title>
            <p>
              The mangrove ecosystem of Tumbes, on the northern coast of Peru,
              is located near the boundary between the warm waters of the
              equatorial Pacific and the cool Humboldt current system. Because
              of this, it is strongly affected by the climate variability
              associated with El Niño. This ecosystem is very important for the
              Tumbes region as a direct source of livelihood and food and
              because of the environmental services it provides. In addition to
              natural phenomena, it experiences pressures from human activities,
              like contamination and deforestation by increasing shrimp farming
              activities. More alarmingly, overexploitation of one of its most
              priced resources, the "black conch" (Anadara tuberculosa), has led
              to a
              <a
                href="http://www.imarpe.gob.pe/tumbes/especies_comerciales/invertebrados/concha_negra.pdf"
                target="_blank"
                rel="nofollow"
                >decline of 80% in its capture over the last 14 years</a
              >. Sustainable management requires basic scientific information
              that is currently lacking and preparing for the looming impacts of
              climate change would require even a greater depth in the
              understanding of this ecosystem and its environmental controls.
            </p>
            <p>
              This research project aims at filling this void. As required by
              the nature of the problem, it will be carried out by a
              multi-disciplinary and inter-institutional team of researchers in
              the physical, biological, and social sciences, in coordination
              with stakeholders and authorities. Several students will
              contribute to the research through the development of their
              thesis. A range of&nbsp; scenarios associated with future climate
              variability and change, as well as socioeconomical trends, will be
              developed and used in the elaboration of an integrated adaptation
              plan for the mangrove ecosystem and the associated population.
            </p>
            <p>
              <b>Ejecutor</b>:
              <a href="http://www.igp.gob.pe" target="_blank" rel="nofollow"
                >Instituto Geofísico del Perú</a
              >
            </p>
            <p>
              <b>Socios</b>:
              <a
                href="http://www.sernanp.gob.pe/"
                target="_blank"
                rel="nofollow"
                >SERNANP</a
              >, <a href="http://www.imarpe.gob.pe" rel="nofollow">IMARPE</a>,
              UNMSM Biología, otros
            </p>
            <p>
              <b>Financiamiento</b>:
              <a href="http://www.idrc.ca" target="_blank" rel="nofollow"
                >IDRC</a
              >
              (Canadá)
            </p>
          </Navtab>
          <Navtab id="esquema-conceptual">
            <Title tag="h2">
              Esquema conceptual (2013)
            </Title>
            <Figure>
              <Image
                fullsize
                :srcsets="['/images/proyectos/manglares/esquema2013.gif']"
                title="Cogollo Estero Camarones (SNLMT)"
              />
            </Figure>
          </Navtab>
          <Navtab id="objetivos">
            <Title tag="h2">
              Objetivos
            </Title>
            <Title tag="h4" theme="secondary">Objetivo principal</Title>
            <p>
              Fortalecer la capacidad de adaptación a la variabilidad y cambio
              climático en el ecosistema de manglares de Tumbes.
            </p>
            <Title tag="h4" theme="secondary">Objetivos específicos</Title>
            <List tag="ol">
              <li>
                Fortalecer y profundizar los estudios sobre los procesos físicos
                vinculados a la variabilidad y cambio climático en el ecosistema
                de los manglares de Tumbes.
              </li>
              <li>
                Analizar los impactos que los procesos físicos vinculados a la
                variabilidad y cambio climático generan sobre el ecosistema de
                los manglares de Tumbes y la población.
              </li>
              <li>
                Analizar la vulnerabilidad socioeconómica de la población
                (hombres, mujeres y jóvenes) vinculada al ecosistema, y valorar
                en términos económicos los servicios ambientales brindados por
                el ecosistema de los manglares de Tumbes.
              </li>
              <li>
                Fortalecer capacidades locales de investigación en temas de
                adaptación al cambio climático, y difundir los resultados del
                proyecto a la población, instituciones locales, autoridades y
                medios científicos.
              </li>
              <li>
                Desarrollar un plan integrado de estrategias de adaptación,
                incluyendo a los actores clave involucrados (población,
                instituciones locales y autoridades) que permita incorporar el
                tema de adaptación al cambio climático en los instrumentos de
                gestión local, regional y nacional.
              </li>
            </List>
          </Navtab>
          <Navtab id="problema-justificacion">
            <Title tag="h2">
              Problema y Justificación
            </Title>
            <p>
              Los manglares son ecosistemas de zonas tropicales que toman su
              nombre del mangle, un árbol adaptado fisiológica y anatómicamente
              a las aguas salinas y que puede crecer en regiones donde el aporte
              de agua dulce de ríos se mezcla con el agua marina y que, debido a
              la acción de las mareas, son inundados y desaguados alternadamente
              por las mareas lo cual es crítico para la dinámica del ecosistema.
              El ecosistema manglar de la costa norte del Perú constituye el
              límite sur de este ecosistema en el Pacífico oriental. La mayor
              parte de este se ubica en la región de Tumbes (200,000 habitantes,
              466,920 ha), y si bien el área de este ecosistema es pequeña
              (4,801 ha), representa una importante fuente directa de ingresos
              económicos para la región debido a sus recursos hidrobiológicos,
              particularmente la concha negra (Anadara tuberculosa) y el
              cangrejo manglero (Ucides occidentalis) que son muy apreciados en
              la gastronomía nacional, y por el atractivo turístico de la zona.
              Además brinda servicios ambientales, como control de inundaciones,
              retención de nutrientes y sedimentos (Gonzales, 2004),
              restauración de la calidad del agua necesaria para la actividad
              langostinera (FUNDECOR, 2008), entre otros. En total, se estima
              que su valor es de cerca a 4 millones de dólares americanos por
              año (INRENA, 2007) y es el sustento de aproximadamente 10% de la
              población económicamente activa de la región (AB Sustenta, 2002).
            </p>
            <p>
              A partir de la década de 1980, la expansión de la actividad
              langostinera hacia los manglares se convierte en una fuerte
              amenaza (INRENA, 2007). En 1988 se crea el Santuario Nacional Los
              Manglares de Tumbes (SNLMT), área natural protegida que contiene
              cerca de 2,972 ha del ecosistema, cuyo objetivo es "la protección
              del Bosque de Manglar, único ecosistema representativo del Norte
              Peruano, así como la gran cantidad y diversidad de invertebrados
              acuáticos de importancia económica", así como para "incentivar la
              recreación y aumentar las corrientes turísticas en los lugares
              aledaños, mediante su uso en el turismo de vista e incentivar la
              recreación y el turismo" (Decreto Supremo 018-88-AG). Además, el
              Santuario es un hábitat de aves acuáticas que en 1997 fue
              declarado como un sitio RAMSAR de importancia internacional. Sin
              embargo, en la actualidad el ecosistema continúa experimentando
              presiones asociadas a la sobreexplotación de sus recursos,
              contaminación del agua y, por ende, de todo el ecosistema, además
              de fenómenos naturales, como los eventos El Niño (INRENA, 2007).
            </p>
            <p>
              La presión humana sobre el ecosistema tiene múltiples orígenes. El
              más evidente es la actividad langostinera, la cual deforesta el
              manglar para la creación de pozas y el uso de la madera, además de
              contaminar el agua con sus desagües y modificar los flujos de agua
              con la creación de canales. También se ha detectado contaminación
              con metales pesados en los recursos hidrobiológicos de consumo
              directo, que podría tener un impacto sobre la salud de la
              población (Echevarría, 2003; ARPSON Perú, 2010), y la
              contaminación con residuos sólidos de parte de la población
              (INRENA, 2007). En cuanto a los recursos hidrobiológicos, los
              extractores mismos estiman que las especies más vulnerables son la
              concha negra y el cangrejo manglero y que estas no soportarían el
              nivel actual de explotación (Malca, 2005). La captura de concha
              negra muestra una fuerte tendencia negativa: la captura del 2010
              fue cerca a 80% menor que la de 1996. Esta situación está
              generando una gran preocupación entre las diferentes asociaciones
              de extractores: concheros, cangrejeros y pescadores, cuya
              principal actividad económica depende directamente del manglar (J.
              Bustamante, presidente del Comité de Gestión del SNLMT,
              comunicación personal). Actividades económicaa complementarias a
              la extracción, como la preparación de comidas típicas para
              turistas, rol predominantemente de mujeres, también son afectadas.
              Además, existe incertidumbre sobre el futuro de los jóvenes debido
              al límite máximo en el número de extractores en las asociaciones
              formales.
            </p>
            <p>
              Las amenazas naturales están asociadas al clima. La región de
              Tumbes se ubica inmediatamente al norte de la transición entre las
              aguas cálidas ecuatoriales y la corriente fría de Humboldt al sur.
              Climáticamente, esta región es muy dinámica en la escala
              interanual debido a la influencia del fenómeno El Niño, que además
              del aumento de la temperatura y del nivel del mar costero, puede
              producir lluvias mucho mayores que lo normal. En particular,
              durante los eventos El Niño extraordinarios 1982-83 y 1997-98, el
              río Tumbes presentó un caudal medio anual de cerca al 500 y 300%
              de lo normal, respectivamente. Esto a su vez se asocia a
              transporte anómalo de sedimentos que produce la colmatación de
              esteros (Huerta et al., 1999; INRENA, 2007), y que durante El Niño
              1997-98 aparentemente resultó en la reducción de hábitat y de las
              poblaciones de cangrejo manglero y concha negra (Malca, 2005), y
              la muerte de mangles por el bloqueo de la inundación por las
              mareas (H. Preciado, comunicación personal). Otro impacto de los
              caudales fluviales anómalos se relaciona con la reducción en
              salinidad debido al aporte de agua fresca, que en condiciones de
              mucho caudal puede llevar a desplazar al agua marina (ARPSON Perú,
              2010), lo cual podría haber producido la muerte de un número
              significativo de conchas negras durante los eventos El Niño,
              particularmente durante el evento 1982-83 (J. Bustamante,
              comunicación personal).
            </p>
            <p>
              Con el calentamiento global, hay un conjunto de cambios
              ambientales asociados. Entre estos están el calentamiento general
              del planeta, el aumento de nivel del mar global, el derretimiento
              general del hielo marino y continental, entre otros (IPCC, 2007).
              Si bien los ecosistemas de manglares son por naturaleza resiliente
              ante variaciones ambientales, se estima a grandes rasgos que el
              cambio climático global, principalmente a través del aumento del
              nivel del mar (Gilman et al., 2008), podría producir una pérdida
              de área de entre 10 y 15% a nivel global hacia el año 2100
              (Alongi, 2008). Sin embargo, la región de Tumbes tiene condiciones
              climáticas únicas y a la escala local que es la importante para
              determinar los posibles impactos futuros en los manglares de
              Tumbes, existe mucha más incertidumbre. Por ejemplo, durante El
              Niño, la lluvia anormal en la costa norte del Perú está asociada a
              un calentamiento local de la temperatura del mar (Woodman, 1999;
              Takahashi, 2004), pero el calentamiento en el Pacífico ecuatorial
              central tiene el efecto contrario. Por lo tanto, el efecto neto
              del calentamiento global dependería de un sutil balance de los dos
              efectos, si bien los modelos climáticos sugieren que se esperaría
              un aumento moderado de las precipitaciones (Takahashi y Palacios,
              manuscrito en preparación). Si la lluvia aumentara en el futuro,
              la salinidad del manglar disminuiría, pero esto podría ser
              compensado en parte por el aumento en la evaporación asociado al
              calentamiento. Por otro lado, el aumento en el nivel de mar puede
              llevar a una pérdida de las áreas alternadamente inundadas y
              desaguadas, pero un aumento de aporte fluvial de sedimentos podría
              contrarrestar este efecto. Desde otra perspectiva, sin embargo, el
              cambio climático progresivo supuesto anteriormente podría no ser
              tan relevante como un cambio en la variabilidad. Por ejemplo, un
              aumento en la probabilidad de ocurrencia de eventos como los de
              1982-83 y 1997-98 podría tener consecuencias drásticas sobre la
              supervivencia del ecosistema y el estado del conocimiento actual
              no permite afirmar si esto es un escenario probable (Takahashi et
              al., 2011).
            </p>
            <p>
              Por otro lado, los ecosistemas de manglares son unos de los más
              ricos en carbono en los trópicos, particularmente gracias al
              almacenamiento subterráneo, por lo cual la deforestación a nivel
              global de estos es responsable de aproximadamente 10% de las
              emisiones de gases invernadero por deforestación (Donato et al.,
              2011). Por esto, la conservación de los manglares puede jugar un
              rol importante en la mitigación del cambio climático.
            </p>
            <p>
              Si bien la incertidumbre es alta, es claro que el cambio
              climático, combinado con las otras presiones sobre el ecosistema,
              puede tener impactos severos sobre los manglares (Gilman et al.
              2008). Por lo mismo, la población que depende de la extracción de
              recursos de los manglares, y las actividades complementarias como
              turismo y gastronomía, son altamente vulnerables. Un manejo
              sostenible de los manglares que reduzca los impactos de la
              variabilidad y cambio climático y las otras presiones, requiere
              información científica base. Por ejemplo, no se conoce cuáles son
              los factores ambientales que determinan que hayan zonas dentro del
              manglar donde las conchas negras son más numerosas, llamados
              "conchales" por los extractores (J. Bustamante, comunicación
              personal) aunque podrían estar asociados a diferencias en las
              condiciones sedimentológicas y/o geoquímicas. Debido a que los
              recursos hidrobiológicos en Tumbes podrían haber desarrollado
              adaptaciones locales (J. Bustamante, comunicación personal) que no
              permitirán en general la aplicación directa del conocimiento
              científico obtenido en otras regiones, este tipo de información
              sería esencial para implementar medidas de adaptación como la
              acuicultura de la concha negra (INRENA, 2007). La extensión de los
              periodos de veda podrían favorecer a los recursos hidrobiológicos
              y muy recientemente la Asociación Peruana de Gastronomía empezó a
              promover una veda indefinida de la concha negra (APEGA, 2011). Sin
              embargo, una extensión de la veda requeriría proveer de empleo
              temporal a las poblaciones que dependen de este recurso (IMARPE
              Tumbes, 2011). El turismo sostenible es una actividad con enorme
              potencial, pero que requiere un desarrollo integral que por ahora
              no posee (Murrugarra, 2008).
            </p>
            <p>
              A pesar de su importancia económica y estratégica, la región
              Tumbes y, en particular, su ecosistema de manglares cuentan con
              pocos estudios científicos y por lo tanto es poco lo que se conoce
              de sus aspectos climáticos, geológicos, biológicos,
              socioeconómicos y culturales. Muchos de los estudios existentes se
              desarrollaron como consultorías, que por su naturaleza no permiten
              la profundidad necesaria y que no pasan por el sistema de revisión
              por pares a nivel internacional asociado a la publicación en
              revistas científicas.
            </p>
            <p>
              Sin una línea base científicamente sólida sobre la situación
              actual, no es posible realizar adecuadamente la valoración
              económica del ecosistema (Gonzales, 2004), ni estimar las posibles
              consecuencias que el cambio climático tendrá sobre los recursos
              hídricos de la zona (por ej. disponibilidad de agua potable para
              la población y agua fresca para el manglar), ni sobre los eventos
              meteorológicos extremos y sus efectos directos (por ej.
              inundaciones, colmatación de esteros), y menos aún sobre la
              respuesta de los ecosistemas a estos cambios. Sin conocimiento
              sobre el posible impacto del cambio climático, no es posible
              estimar el costo potencial de estos impactos, ni plantear medidas
              efectivas de adaptación al cambio climático.
            </p>
            <p>
              Al respecto, se generan grandes interrogantes que este proyecto
              pretende ayudar a contestar: ¿cuál es el estado actual y la
              dinámica del ecosistema de manglares de Tumbes?, ¿cuáles han sido
              los impactos de la variabilidad climática (particularmente el
              fenómeno El Niño) sobre este?, ¿cuáles son las vulnerabilidades
              socioeconómicas de los extractores y sus familias ante cambios en
              la variabilidad y el cambio climático?, ¿cuáles han sido las
              respuestas de los actores claves como pobladores y autoridades
              ante eventos como el fenómeno El Niño?, ¿cuáles serían los
              impactos futuros del cambio climático en dichos ecosistemas?. y
              finalmente, ante posibles escenarios de cambio climático ¿cuáles
              serían las medidas que en forma flexible y funcional permitan a la
              población vulnerable, instituciones locales y autoridades
              responsables el adaptarse a los cambios futuros?
            </p>
            <p>
              Este proyecto aborda los cuatro temas prioritarios de la
              convocatoria 2011 del programa CCA del IDRC. El IDRC actualmente
              está financiando el proyecto multidisciplinario de
              investigación-acción "Enhancing Resilience of Rural Communities to
              Drought, Floods and Frost in the Mantaro Valley, Peru" (#105567)
              del Instituto Geofísico del Perú en los Andes peruanos. La
              presente propuesta aprovecha esta experiencia, pero se distingue,
              además de tener un ámbito geográfico y problemáticas muy
              distintas, de involucrar más fuertemente a otras instituciones de
              investigación, incluyendo universidades e instituciones de
              investigación locales, en el desarrollo de los estudios. Además,
              se incorporan desde un inicio a instituciones asociadas a la
              gestión del ecosistema, tales como SERNANP (a cargo del SNLMT),
              MEDA (administradora del SNLMT), el Comité de Gestión del SNLMT
              (que representa a concheros y cangrejeros, así como a otras
              instituciones y actores de base), y el Gobierno Regional de
              Tumbes, lo cual permitirá que los resultados de los estudios se
              articulen efectivamente en el desarrollo de políticas para el
              manejo del ecosistema.
            </p>
          </Navtab>
          <Navtab id="tabla-mareas">
            <Title tag="h2">
              Tabla de Mareas
            </Title>
            <Figure>
              <Image
                linked
                :srcsets="[
                  '/images/proyectos/manglares/tabla-marea-setiembre-2012.jpg'
                ]"
                title="Tabla de mareas 2012"
              />
              <template #figcaption>
                Tabla de Mareas Setiembre 2012
              </template>
            </Figure>
            <a :href="`https://www.igp.gob.pe/programas-de-investigacion/ciencias-de-la-atmosfera-e-hidrosfera/resources/proyectos/manglares/Tabla mareas Zorritos 2012.pdf`" target="_blank">
              Tabla de mareas Zorritos 2012.pdf
            </a>
          </Navtab>
          <Navtab id="tesis-sustentadas">
            <Title tag="h2">
              Tesis sustentadas
            </Title>
            <TableUi>
              <thead>
                <tr>
                  <th>Tesista</th>
                  <th>Título de tesis</th>
                  <th>Grado alcanzado</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Morera, Sergio</td>
                  <td>
                    <a
                      href="https://repositorio.igp.gob.pe/handle/20.500.12816/925"
                      target="_blank"
                    >
                      Magnitud, frecuencia y factores que controlan los flujos
                      sedimentarios desde los Andes Centrales Occidentales hacia
                      el océano Pacífico peruano
                    </a>
                  </td>
                  <td>
                    Doctor en Recursos Hídricos, Universidad Nacional Agraria La
                    Molina, Lima
                  </td>
                </tr>
                <tr>
                  <td>Ramos, Yakelyn</td>
                  <td>
                    <a
                      href="https://repositorio.igp.gob.pe/handle/20.500.12816/47"
                      target="_blank"
                    >
                      Estimación del efecto del cambio climático en la
                      precipitación en la costa norte del Perú usando
                      simulaciones de modelos climáticos globales
                    </a>
                  </td>
                  <td>
                    Ingeniera Meteoróloga, Universidad Nacional Agraria La
                    Molina, Lima
                  </td>
                </tr>
                <tr>
                  <td>León, Karen</td>
                  <td>
                    <a
                      href="https://repositorio.igp.gob.pe/handle/20.500.12816/922"
                      target="_blank"
                    >
                      Análisis espacio-temporal de las precipitaciones y
                      caudales durante los eventos El Niño (1982-83 y 1997-98)
                      en la costa norte peruana
                    </a>
                  </td>
                  <td>
                    Ingeniero Agrícola, Universidad Nacional Agraria La Molina,
                    Lima
                  </td>
                </tr>
                <tr>
                  <td>Pérez, Alexander</td>
                  <td>
                    <a
                      href="https://repositorio.igp.gob.pe/handle/20.500.12816/920"
                      target="_blank"
                    >
                      Influencia del régimen hídrico y de las condiciones
                      geoquímicas sobre la comunidad y el flujo energético del
                      meiobentos metazoario de los sedimentos intermareales y
                      submareales de los Manglares de Tumbes.
                    </a>
                  </td>
                  <td>
                    Magíster en Ciencias del Mar, Universidad Peruana Cayetano
                    Heredia, Lima
                  </td>
                </tr>
                <tr>
                  <td>Parra, Claudia</td>
                  <td>
                    <a
                      href="https://repositorio.igp.gob.pe/handle/20.500.12816/917"
                      target="_blank"
                    >
                      Efecto de la altitud del terreno sobre la estructura y
                      distribución espacial de las comunidades vegetales del
                      Santuario Nacional Los Manglares de Tumbes (SNLMT)
                    </a>
                  </td>
                  <td>
                    Bióloga, Universidad Nacional Agraria La Molina, Lima
                  </td>
                </tr>
                <tr>
                  <td>Feijoo, Alder</td>
                  <td>
                    <a
                      href="https://repositorio.igp.gob.pe/handle/20.500.12816/4628"
                      target="_blank"
                    >
                      Identificación de Impactos de la actividad agrícola para
                      el período 1985 – 2014, en la Zona de Amortiguamiento del
                      SNLMT, distritos de Aguas Verdes y Zarumilla
                    </a>
                  </td>
                  <td>
                    Ingeniero Forestal y del Medio Ambiente, Universidad
                    Nacional de Tumbes, Tumbes
                  </td>
                </tr>
                <tr>
                  <td>Prado, Roxana</td>
                  <td>
                    <a
                      href="https://repositorio.igp.gob.pe/handle/20.500.12816/4630"
                      target="_blank"
                    >
                      Capacidad de carga turística de los circuitos turísticos
                      del Santuario Nacional Los Manglares de Tumbes y del
                      circuito turístico de Puerto Pizarro, Tumbes 2013-2014
                    </a>
                  </td>
                  <td>
                    Ingeniero Forestal y del Medio Ambiente, Universidad
                    Nacional de Tumbes, Tumbes
                  </td>
                </tr>
                <tr>
                  <td>Quincho, Janet</td>
                  <td>
                    <a
                      href="https://repositorio.igp.gob.pe/handle/20.500.12816/921"
                      target="_blank"
                    >
                      Estudio experimental del transporte de sedimentos en suspensión 
                      y fondo, y comparación con modelos teóricos en los ríos Puyango, 
                      Tumbes y Zarumilla
                    </a>
                  </td>
                  <td>
                    Ingeniero Agrícola, Universidad Nacional Agraria La Molina, Lima
                  </td>
                </tr>
              </tbody>
            </TableUi>
          </Navtab>
          <Navtab id="zona-estudio">
            <Title tag="h2">Zona de estudio</Title>
            <iframe
              src="https://www.google.com/maps/d/embed?mid=1N5qLuE39LvhljdSbEHG4VG8LllM"
              width="100%"
              height="600"
            ></iframe>
          </Navtab>
          <Navtab id="datos">
            <Title tag="h2">
              Datos
            </Title>
            <List tag="ol">
              <a
                  :href="`https://www.igp.gob.pe/programas-de-investigacion/ciencias-de-la-atmosfera-e-hidrosfera/resources/proyectos/manglares/DATOS.rar`"
                  target="_blank"
                  >
                    <li>Descarga Datos y Difusión</li>
              </a>
            
            </List>  
          </Navtab>
        </ColumnUi>
        <ColumnUi md="3">
          <Nav :items="navItems" title="Contenido" />
        </ColumnUi>
      </Row>
    </Container>
  </main>
</template>
<script>
import Trail from "@/components/common/Trail";
import Title from "@/components/ui/atoms/Title";
import List from "@/components/ui/objects/List";
import Container from "@/components/ui/objects/Container";
import TableUi from "@/components/ui/objects/TableUi";
import Figure from "@/components/ui/objects/Figure";
import Image from "@/components/ui/objects/Image";
import Nav from "@/components/ui/objects/Nav";
import Navtab from "@/components/ui/objects/Navtab";
import Row from "@/components/ui/atoms/Row";
import ColumnUi from "@/components/ui/atoms/ColumnUi";
export default {
  components: {
    Trail,
    Title,
    List,
    Container,
    TableUi,
    Figure,
    Image,
    Nav,
    Navtab,
    Row,
    ColumnUi
  },
  setup() {
    const navItems = [
      {
        path: "#resumen",
        tag: "Resumen"
      },
      {
        path: "#abstract",
        tag: "Abstract"
      },
      {
        path: "#esquema-conceptual",
        tag: "Esquema Conceptual"
      },
      {
        path: "#objetivos",
        tag: "Objetivos"
      },
      {
        path: "#problema-justificacion",
        tag: "Problema y Justificación"
      },
      {
        path: "#tabla-mareas",
        tag: "Tabla de mareas Septiembre 2012"
      },
      {
        path: "#tesis-sustentadas",
        tag: "Tesis Sustentadas"
      },
      {
        path: "#zona-estudio",
        tag: "Zona de estudio"
      },
      {
        path: "#datos",
        tag: "Datos"
      }
    ];
    return {
      navItems
    };
  }
};
</script>
